import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FirebaseError } from '@angular/fire/app';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import Debug from 'debug';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap } from 'rxjs/operators';
import * as fromActions from '../actions/auth.actions';
import { CreateUserDto } from '../dtos/createUserDto.interface';
import { GetUserDto } from '../dtos/getUserDto.type';
import { IChangePasswordRequest } from '../interfaces/change-password-request.interface';
import { IResetUserRequest } from '../interfaces/reset-user-request.interface';
import { ISetOrResetPasswordRequest } from '../interfaces/set-or-reset-password-request.interface';
import { IUserPreferences } from '../interfaces/user-preferences.interface';
import { IVerifyOOBCode } from '../interfaces/verifyOOBCode.interface';
import { AuthService } from '../services/auth.service';
import { userNotificationsActions } from '@modeso/aumico-lib-core-fe';
const debug = Debug('modeso:aumico-lib-users-fe:AuthEffects');

@Injectable()
export class AuthEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly service: AuthService,
    ) {}

    login$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.login.do.type),
            mergeMap((payload) => {
                return this.service.login$(payload).pipe(
                    map(() => {
                        return fromActions.login.success();
                    }),
                    catchError((error: FirebaseError) => {
                        return of(fromActions.login.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    connectNotificationsOnLogin$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.login.success.type),
            mergeMap(() =>
                this.service.getIdToken().pipe(
                    filter((token): token is string => Boolean(token)),
                    map((token) => userNotificationsActions.connectNotificationsSocket({ token })),
                    catchError(() => of(userNotificationsActions.disconnectNotificationsSocket())),
                ),
            ),
        ),
    );

    // Also attempt socket connection when user explicitly opens notifications (load action is dispatched)
    connectNotificationsOnLoad$ = createEffect(() =>
        this.actions$.pipe(
            ofType(userNotificationsActions.loadUserNotifications),
            mergeMap(() =>
                this.service.getIdToken().pipe(
                    filter((token): token is string => Boolean(token)),
                    map((token) => userNotificationsActions.connectNotificationsSocket({ token })),
                    catchError(() => of(userNotificationsActions.disconnectNotificationsSocket())),
                ),
            ),
        ),
    );

    logout$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.logout.do.type),
            mergeMap(() => {
                return this.service.logout$().pipe(
                    map(() => {
                        return fromActions.logout.success();
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.logout.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    disconnectNotificationsOnLogout$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.logout.success.type),
            map(() => userNotificationsActions.disconnectNotificationsSocket()),
        ),
    );

    signup$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.signup.do.type),
            mergeMap((payload: { createUserDto: CreateUserDto }) => {
                return this.service.signup$(payload.createUserDto).pipe(
                    map(() => {
                        return fromActions.signup.success();
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.signup.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    resendVerificationMail$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.sendVerificationMail.do.type),
            mergeMap((action: fromActions.ActionWithPayload<string>) => {
                return this.service.resendVerificationMail(action.payload).pipe(
                    map(() => {
                        return fromActions.sendVerificationMail.success();
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.sendVerificationMail.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    getCurrentUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getCurrentUser.do.type),
            mergeMap(() => {
                return this.service.getCurrentUser$().pipe(
                    map((response: GetUserDto) => {
                        return fromActions.getCurrentUser.success({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.getCurrentUser.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    updateCurrentUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.updateCurrentUser.do.type),
            mergeMap((payload) => {
                return this.service.updateCurrentUser$(payload).pipe(
                    map(() => {
                        return fromActions.updateCurrentUser.success(payload);
                    }),
                    catchError((error) => {
                        return of(fromActions.updateCurrentUser.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );
    setPassword$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.password.set.type),
            mergeMap((action: fromActions.ActionWithPayload<ISetOrResetPasswordRequest>) => {
                return this.service.setPassword$(action.payload).pipe(
                    map(() => {
                        return fromActions.password.setSuccess();
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.password.setFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    sendEmailForReset$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.password.sendEmailForReset.type),
            mergeMap((action: fromActions.ActionWithPayload<IResetUserRequest>) => {
                return this.service.sendResetPasswordEmail$(action.payload.email).pipe(
                    map(() => {
                        return fromActions.password.sendEmailForResetSuccess();
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.password.sendEmailForResetFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );
    resetPassword$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.password.update.type),
            mergeMap((action: fromActions.ActionWithPayload<ISetOrResetPasswordRequest>) => {
                return this.service.resetPassword$(action.payload).pipe(
                    map(() => {
                        return fromActions.password.setSuccess();
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.password.setFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );
    changePassword$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.password.change.type),
            mergeMap(({ payload }: fromActions.ActionWithPayload<IChangePasswordRequest>) => {
                return this.service.changePassword$(payload).pipe(
                    map(() => {
                        return fromActions.password.changeSuccess();
                    }),
                    catchError((error: FirebaseError) => {
                        return of(fromActions.password.changeFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    verifyOOB$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.verifyOOBCode.do.type),
            mergeMap(({ payload }: fromActions.ActionWithPayload<IVerifyOOBCode>) => {
                return this.service.verifyOOB$(payload).pipe(
                    map((response) => {
                        return fromActions.verifyOOBCode.success({ payload: response });
                    }),
                    catchError((error: FirebaseError) => {
                        return of(fromActions.verifyOOBCode.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    agreeToTerms$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.agreeToTerms.do.type),
            mergeMap(() => {
                return this.service.agreeToTerms$().pipe(
                    map(() => {
                        return fromActions.agreeToTerms.success();
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.agreeToTerms.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    updateUserPreferences$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.updateUserPreferences.do.type),
            mergeMap(({ payload }: fromActions.ActionWithPayload<IUserPreferences>) => {
                return this.service.updateUserPreferences$(payload).pipe(
                    map(() => {
                        return fromActions.updateUserPreferences.success();
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.updateUserPreferences.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    handleOnLoadAllErrors(error) {
        debug('handleOnLoadAllErrors');
        debug(error);
        return error;
    }
}
